import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePendingTasks } from '../../contexts/PendingTasksContext';
import { Badge } from '../ui/badge';
import { OnboardingTour } from '../OnboardingTour';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import NotificationBell from '../NotificationBell';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Menu, X, Users, LogOut, Eye, XCircle, Shield,
  PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { navItems } from './navConfig';
import { TimeTrackingWidget } from './TimeTrackingWidget';
import { UserMenu } from './UserMenu';
import { CommandPalette } from '../ui/command-palette';
import { ClockReminder } from '../ClockReminder';

const COLLAPSED_KEY = 'teamhub.sidebar.collapsed';

export const DashboardLayout = ({ children, wide = false }) => {
  const { user, logout, isAdmin, hasPermission, realIsAdmin, isInPreview, previewRole, stopPreview } = useAuth();
  const { byPath: pendingByPath, newJobsCount, newNewsCount } = usePendingTasks();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, desktopCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [desktopCollapsed]);

  // Tastatur-Shortcut: Strg+B (Win/Linux) bzw. ⌘+B (Mac) toggelt die Sidebar
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
        // Nicht abfangen, wenn der Nutzer in einem Eingabefeld editiert
        const target = e.target;
        const tag = target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
        e.preventDefault();
        // Auf Mobile öffnet Strg+B die mobile Sidebar, sonst toggle desktop
        if (window.matchMedia('(min-width: 1024px)').matches) {
          setDesktopCollapsed((v) => !v);
        } else {
          setSidebarOpen((v) => !v);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const checkNavPermission = useCallback((item) => {
    // In preview mode, allow access to /permissions so admin can exit
    if (isInPreview && item.path === '/permissions') return true;
    if (isAdmin) return true;
    if (item.adminOnly) return false;
    if (item.permArea) return hasPermission(item.permArea, item.permLevel || 'view');
    return true;
  }, [isAdmin, hasPermission, isInPreview]);

  const filteredNavItems = useMemo(() =>
    navItems.filter(checkNavPermission),
  [checkNavPermission]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const handleStopPreview = () => { stopPreview(); navigate('/permissions'); };
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <CommandPalette />
      <ClockReminder />
      {/* Preview Banner */}
      {isInPreview && (
        <div
          className="sticky top-0 z-[60] px-4 py-2.5 flex items-center justify-between shadow-lg"
          style={{ backgroundColor: previewRole?.color || '#7C3AED', color: '#fff' }}
          data-testid="preview-banner"
        >
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 animate-pulse" />
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="font-semibold text-sm">Rollen-Vorschau:</span>
              <span className="text-sm font-bold">{previewRole?.name}</span>
            </div>
            <span className="text-xs opacity-80 ml-2">
              Sie sehen die App aus Sicht dieser Rolle. API-Zugriffe sind weiterhin als Admin.
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleStopPreview}
            className="gap-2 text-xs font-semibold shadow-md"
            data-testid="btn-stop-preview"
          >
            <XCircle className="h-4 w-4" />
            Vorschau beenden
          </Button>
        </div>
      )}

      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 glass border-b border-border/40">
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <h1 className="font-heading font-bold text-lg text-primary">TeamHub</h1>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />Abmelden</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-0 left-0 z-50 lg:z-0
          ${desktopCollapsed ? 'lg:w-16' : 'lg:w-64'}
          w-64 h-screen bg-card border-r border-border
          flex flex-col
          transform lg:transform-none transition-all duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isInPreview ? 'lg:top-[52px] lg:h-[calc(100vh-52px)]' : ''}
        `} data-testid="sidebar">
          {/* Logo + Desktop Collapse Toggle */}
          <div className={`border-b border-border/40 flex items-center ${desktopCollapsed ? 'lg:justify-center lg:p-3' : 'p-6 justify-between'}`}>
            <Link to="/dashboard" className="flex items-center gap-2 min-w-0" data-testid="sidebar-logo">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-primary-foreground" />
              </div>
              {!desktopCollapsed && (
                <div className="lg:block">
                  <h1 className="font-heading font-bold text-xl text-primary">TeamHub</h1>
                  <p className="text-xs text-muted-foreground">Mitarbeiterverwaltung</p>
                </div>
              )}
            </Link>
            {!desktopCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:inline-flex h-8 w-8 text-muted-foreground hover:text-primary"
                onClick={() => setDesktopCollapsed(true)}
                aria-label="Seitenleiste einklappen (Strg+B)"
                title="Seitenleiste einklappen (Strg+B)"
                data-testid="sidebar-collapse-btn"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Expand button — sichtbar nur im collapsed-Modus */}
          {desktopCollapsed && (
            <div className="hidden lg:flex justify-center py-2 border-b border-border/40">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-primary"
                onClick={() => setDesktopCollapsed(false)}
                aria-label="Seitenleiste ausklappen (Strg+B)"
                title="Seitenleiste ausklappen (Strg+B)"
                data-testid="sidebar-expand-btn"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Navigation */}
          <TooltipProvider delayDuration={0}>
            <nav className={`flex-1 space-y-1 overflow-y-auto ${desktopCollapsed ? 'lg:p-2' : 'p-4'}`}>
              {filteredNavItems.map((item) => {
                const isActive = location.pathname === item.path ||
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                const Icon = item.icon;
                const pendingCount = pendingByPath[item.path] || 0;
                const jobBadge = item.path === '/stellenausschreibungen' ? newJobsCount : 0;
                const newsBadge = item.path === '/news' ? newNewsCount : 0;
                const linkContent = (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                    className={`
                      relative flex items-center gap-3 ${desktopCollapsed ? 'lg:justify-center lg:px-2' : 'px-3'} py-2.5 rounded-lg text-sm font-medium
                      transition-colors duration-200
                      ${isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}
                    `}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                    <span className={`min-w-0 truncate ${desktopCollapsed ? 'lg:hidden' : 'flex-1'}`}>{item.label}</span>
                    {pendingCount > 0 && (
                      <Badge
                        className={`${desktopCollapsed ? 'lg:hidden' : ''} flex-shrink-0 bg-red-500 hover:bg-red-500 text-white text-[10px] h-5 min-w-[20px] px-1.5 leading-none`}
                        data-testid={`nav-badge-${item.path.replace('/', '')}`}
                      >
                        {pendingCount > 99 ? '99+' : pendingCount}
                      </Badge>
                    )}
                    {pendingCount === 0 && jobBadge > 0 && (
                      <Badge
                        className={`${desktopCollapsed ? 'lg:hidden' : ''} flex-shrink-0 bg-primary hover:bg-primary text-primary-foreground text-[10px] h-5 min-w-[20px] px-1.5 leading-none`}
                        data-testid={`nav-newjobs-badge`}
                        title="Neue Stellenausschreibungen seit deinem letzten Besuch"
                      >
                        {jobBadge > 99 ? '99+' : jobBadge}
                      </Badge>
                    )}
                    {pendingCount === 0 && newsBadge > 0 && (
                      <Badge
                        className={`${desktopCollapsed ? 'lg:hidden' : ''} flex-shrink-0 bg-primary hover:bg-primary text-primary-foreground text-[10px] h-5 min-w-[20px] px-1.5 leading-none`}
                        data-testid={`nav-newnews-badge`}
                        title="Neue Neuigkeiten seit deinem letzten Besuch"
                      >
                        {newsBadge > 99 ? '99+' : newsBadge}
                      </Badge>
                    )}
                    {desktopCollapsed && pendingCount > 0 && (
                      <span className="hidden lg:block absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />
                    )}
                    {desktopCollapsed && pendingCount === 0 && jobBadge > 0 && (
                      <span className="hidden lg:block absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />
                    )}
                    {desktopCollapsed && pendingCount === 0 && jobBadge === 0 && newsBadge > 0 && (
                      <span className="hidden lg:block absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />
                    )}
                  </Link>
                );
                return desktopCollapsed ? (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                    <TooltipContent side="right" className="hidden lg:block">{item.label}</TooltipContent>
                  </Tooltip>
                ) : linkContent;
              })}
            </nav>
          </TooltipProvider>

          {!desktopCollapsed && <TimeTrackingWidget />}
          <UserMenu collapsed={desktopCollapsed} />
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 min-h-screen">
          <div className={`${wide ? 'max-w-screen-2xl' : 'max-w-7xl'} mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-20 lg:pb-8 dashboard-content min-w-0`}>{children}</div>
        </main>
      </div>

      {/* Onboarding-Tour: zeigt sich automatisch beim ersten Login */}
      <OnboardingTour />
    </div>
  );
};

export default DashboardLayout;
